import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';

import Link, { LinkStyle, LinkType } from '@/components/Link';
import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import { isDarkTheme } from '@/utils/StyleUtils';
import { mediaQuery } from '@/utils/helpers';
import Tile, { TileType } from '@/components/Tile';

export type TilesBlockType = {
  id: string;
  __typename: string;
  backgroundColor;
  title: string;
  tileBlockSubtitle: {
    subtitle: string;
  };
  primaryLink?: LinkType;
  secondaryLink?: LinkType;
  tilesSide: string;
  tiles: Array<TileType>;
  noContainer: boolean;
};
type Props = TilesBlockType;
type ContentWrapperProps = {
  tilesLeft?: boolean;
};

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2.4rem;

  ${mediaQuery.lg(`
    flex-direction: row;
  `)}
`;

const ContentWrapper = styled.div<ContentWrapperProps>`
  flex-shrink: 0;

  ${({ tilesLeft }) =>
    tilesLeft
      ? `
      ${mediaQuery.lg(`
        flex-basis: 360px;
        order: 2;
      `)}
    `
      : `
      ${mediaQuery.lg(`
        flex-basis: 360px;
      `)}
    `}
`;

const TilesWrapper = styled.ul`
  flex-shrink: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.4rem;
  padding: 0;
  list-style: none;

  ${mediaQuery.lg(`
    flex-basis: 768px;
    justify-content: flex-end;
  `)}
`;

const CTAWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 2rem;
  align-items: center;
  flex-wrap: wrap;
`;

const TilesBlock: React.FC<Props> = ({
  id,
  title,
  tileBlockSubtitle,
  backgroundColor,
  tilesSide,
  primaryLink,
  secondaryLink,
  tiles,
  noContainer,
}) => {
  const content = (
    <MainWrapper>
      <ContentWrapper tilesLeft={tilesSide === 'left'}>
        <Title
          level='h2'
          size='xl'
          margin='0 0 2rem'
          lightColor={isDarkTheme(backgroundColor)}
        >
          {title}
        </Title>
        {tileBlockSubtitle && (
          <Paragraph
            color={isDarkTheme(backgroundColor) ? 'White' : 'GreyBlue'}
            weight='semiBold'
            margin='0 0 4rem'
          >
            {tileBlockSubtitle.subtitle}
          </Paragraph>
        )}
        <CTAWrapper>
          <Link {...primaryLink} linkStyle={LinkStyle.PRIMARY} />
          <Link {...secondaryLink} linkStyle={LinkStyle.SECONDARY} />
        </CTAWrapper>
      </ContentWrapper>
      <TilesWrapper>
        {React.Children.toArray(
          tiles.map((tile) => {
            return Tile(tile);
          }),
        )}
      </TilesWrapper>
    </MainWrapper>
  );

  return (
    <BlockWrapper
      id={id}
      padding={noContainer ? 'small' : 'normal'}
      color={backgroundColor}
    >
      {noContainer ? content : <Container>{content}</Container>}
    </BlockWrapper>
  );
};

export default TilesBlock;

export const tilesBlockFragmentQuery = graphql`
  fragment TilesBlockFragment on ContentfulTilesBlock {
    id
    __typename
    backgroundColor
    title
    tileBlockSubtitle: subtitle {
      subtitle
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
    tilesSide
    tiles {
      ...TileFragment
    }
  }
`;
